/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnDestroy, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { Customer } from '@wsphere/customers/domain';
import { Distributor } from '@wsphere/distributors/domain';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { suggestEmailDomain } from '@vsolv/common/email/domain';
import { CustomerService } from '../../services';

export interface CustomerInfo {
  email: string;
  name: string;
  phone: string;
}
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-customer-details-input',
  templateUrl: './customer-details-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CustomerDetailsInputComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => CustomerDetailsInputComponent),
    },
  ],
})
export class CustomerDetailsInputComponent implements ControlValueAccessor, Validator, OnDestroy {
  constructor(
    private fb: FormBuilder,
    private firebaseSvc: FirebaseService,
    private customerSvc: CustomerService
  ) {
    // Debounce email-input events so we hit the API once per typing burst
    // instead of once per keystroke. distinctUntilChanged drops repeats
    // (e.g. user types, deletes, retypes the same string).
    this.emailInput$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(value => {
      // checkForCustomer is async; swallow rejections here so a failed lookup
      // doesn't surface as an unhandled promise rejection.
      void this.checkForCustomer(value).catch(error => console.error('Customer lookup failed', error));
    });
  }

  @PropertyListener('staffView') staffView$ = new BehaviorSubject(false);
  @Input() staffView = false;

  @Input() distributor: Distributor.Model | null = null;

  @Input() set value(value: CustomerInfo | undefined) {
    this.writeValue(value || null);
  }

  @Output() customerAdded = new EventEmitter<Customer.Model>();

  subscription?: Subscription;

  validNumber = true;
  customer: Customer.Model | null = null;
  /** Suggested correction when the entered email's domain looks like a typo. */
  suggestedEmail: string | null = null;

  /** Stream of raw email-input events, debounced before triggering a lookup. */
  readonly emailInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  /**
   * The cached customer, but only if its email still matches what's currently
   * in the field. Both the "already exists" hint and the autofill click use this —
   * so the hint can't show stale data and the click can't emit a mismatched customer.
   */
  get matchedCustomer(): Customer.Model | null {
    if (!this.customer) return null;
    const currentEmail = this.form.get('email')?.value?.trim().toLowerCase();
    const customerEmail = this.customer.email?.trim().toLowerCase();
    return currentEmail === customerEmail ? this.customer : null;
  }

  form = this.fb.group({
    email: [{ value: null as string | null, disabled: false }, [Validators.required, Validators.email]],
    name: [{ value: null as string | null, disabled: false }, Validators.required],
    phone: [{ value: null as string | null, disabled: false }, [Validators.required]],
  });

  signedIn$ = this.firebaseSvc.authUser$;
  disableEmail$ = combineLatest([this.staffView$, this.signedIn$]).pipe(
    map(([staffView, signedIn]) => {
      if (!staffView && signedIn) {
        this.form.patchValue({ email: signedIn.email });
        this.form.get('email')?.disable();
        return true;
      } else {
        this.form.get('email')?.enable();
        return false;
      }
    })
  );

  onChange = (_customerInfo: CustomerInfo) => {};
  onTouched = () => {};

  writeValue(value: CustomerInfo | null): void {
    if (value) {
      this.form.setValue({
        email: value.email ?? null,
        name: value.name ?? null,
        phone: value?.phone ? new AsYouType('US').input(value.phone) : null,
      });
    }
    this.validateNumber();
  }

  registerOnChange(fn: any): void {
    this.subscription = this.form.valueChanges.subscribe(fn);
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) this.form.disable();
    else this.form.enable();
  }

  /**
   * Called on every email-input keystroke. The typo suggestion is pure-local
   * and updates instantly; the customer lookup is debounced via emailInput$.
   */
  onEmailInput(search: string) {
    this.suggestedEmail = suggestEmailDomain(search);
    this.emailInput$.next(search);
  }

  async checkForCustomer(search: string) {
    if (!search.includes('@') || !search.includes('.')) {
      // Email is no longer in a valid-enough shape to look up. Clear any
      // cached customer so the "already exists" hint stops showing stale data.
      this.customer = null;
      return;
    }

    const customers = this.staffView
      ? await this.customerSvc.checkCustomerForSales(search)
      : await this.customerSvc.listCustomers({ search });

    // Race-condition guard: even with debouncing, responses can arrive in any
    // order if the user keeps typing through the debounce window. By the time
    // this response lands, the email field may have moved on — discard if so.
    if (this.form.get('email')?.value !== search) return;

    if (customers.items?.length === 1) this.customer = customers.items[0];
    else this.customer = null;
  }

  /** Accept the suggested email correction and clear the prompt. */
  applyEmailSuggestion() {
    if (!this.suggestedEmail) return;
    const corrected = this.suggestedEmail;
    this.form.patchValue({ email: corrected });
    this.suggestedEmail = null;
    // Re-run the existence check against the corrected email. Swallow errors
    // here for the same reason as the debounced subscription above.
    void this.checkForCustomer(corrected).catch(error => console.error('Customer lookup failed', error));
  }

  emitCustomer() {
    // Only emit if the cached customer still matches the current email field.
    // The hint condition uses the same getter, so this branch should only
    // ever fire when the click is genuinely valid.
    const customer = this.matchedCustomer;
    if (customer) this.customerAdded.emit(customer);
  }

  validateNumber() {
    if (!this.form.get('phone')?.value) {
      this.validNumber = true;
      return;
    }
    try {
      const phone = new AsYouType('US').input(this.form.get('phone')?.value as string);
      this.validNumber = isValidPhoneNumber(phone, 'US');
      return;
    } catch (e) {
      console.log(e);
    }
    this.validNumber = false;
  }

  phoneNumberKeyUp(phone: any) {
    if (phone.key === 'Backspace') return;
    this.formatPhoneNumber();
  }

  formatPhoneNumber() {
    const phoneNumber = this.form.get('phone')?.value;
    if (phoneNumber) {
      this.form.patchValue({ phone: new AsYouType('US').input(phoneNumber) });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    if (this.form.valid) {
      return null;
    }
    let errors = {};
    if (this.form.controls['email'].errors) {
      errors = { email: this.form.controls['email'].errors };
    }
    if (this.form.controls['name'].errors) {
      errors = { name: this.form.controls['name'].errors };
    }
    if (this.form.controls['phone'].errors) {
      errors = { phone: this.form.controls['phone'].errors };
    }
    return errors;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.emailInput$.complete();
  }
}
